/**
 * `<Logo />` — componente da marca Planuze (ADR-0075).
 *
 * Variantes:
 * - `'black'`: logo escuro (uso em fundos claros).
 * - `'white'`: logo claro (uso em fundos escuros).
 * - `'auto'` (default): resolve via `prefers-color-scheme` ou
 *   `<html data-theme>` via media query. Em SSR/build estático,
 *   `auto` mostra `black` por padrão e troca em runtime via CSS.
 *
 * `new URL(..., import.meta.url)` evita que consumidores do barrel público
 * precisem carregar declarações ambiente para `.png` durante typecheck.
 */
import type { ImgHTMLAttributes, JSX } from 'react';
import { cn } from '../lib/utils.js';

export type LogoVariant = 'black' | 'white' | 'auto';

export interface LogoProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  readonly variant?: LogoVariant;
}

const ALT_DEFAULT = 'Planuze';
const logoBlackUrl = new URL('../assets/logo/black.png', import.meta.url).href;
const logoWhiteUrl = new URL('../assets/logo/white.png', import.meta.url).href;

/** Logo "glass" (marca 3D translúcida) — hero para superfícies escuras/de marca (ex.: painel do
    login do backoffice). URL exportada para consumo direto em `<img>`. */
export const glassLogoUrl = new URL('../assets/logo/glass-planuze.png', import.meta.url).href;

export function Logo({
  variant = 'auto',
  alt = ALT_DEFAULT,
  className,
  ...props
}: LogoProps): JSX.Element {
  if (variant === 'black') {
    return <img src={logoBlackUrl} alt={alt} className={className} {...props} />;
  }
  if (variant === 'white') {
    return <img src={logoWhiteUrl} alt={alt} className={className} {...props} />;
  }
  // Auto: dois <img> (um para cada tema), CSS controla qual aparece via
  // `prefers-color-scheme` + `<html data-theme>`.
  return (
    <>
      <img src={logoBlackUrl} alt={alt} className={cn('block dark:hidden', className)} {...props} />
      <img src={logoWhiteUrl} alt={alt} className={cn('hidden dark:block', className)} {...props} />
    </>
  );
}
